// The pick queue's arrangement (site setting picking.queue): one flat list in pick order, or
// one group per carrier with the carrier that collects earliest first (owner 2026-10-29:
// configurable; Kalundgren groups per carrier). The carriers and their normal pickup times
// are the site's own list; an order whose carrier is not listed goes last, one without a
// carrier last of all.

export interface QueueOrder {
  carrier: string | null;
  pickupAt: string | null;
}

export interface CarrierSetting {
  name: string;
  pickupTime: string | null;
}

export interface QueueGroup<T extends QueueOrder> {
  /** The carrier's name, or null for orders without one. */
  carrier: string | null;
  /** The site's normal pickup time for the carrier (HH:MM), when listed. */
  pickupTime: string | null;
  orders: T[];
}

export function groupOrders<T extends QueueOrder>(orders: T[], picking: { queue?: "flat" | "carrier"; carriers?: CarrierSetting[] } | null | undefined): QueueGroup<T>[] {
  if ((picking?.queue ?? "flat") !== "carrier") return [{ carrier: null, pickupTime: null, orders }];
  const carriers = picking?.carriers ?? [];
  const rank = (name: string | null): [number, string] => {
    if (name === null) return [3, ""];
    const i = carriers.findIndex((c) => c.name.toLowerCase() === name.toLowerCase());
    if (i < 0) return [2, name.toLowerCase()];
    const time = carriers[i]!.pickupTime;
    return time ? [0, time] : [1, name.toLowerCase()];
  };
  const groups = new Map<string, QueueGroup<T>>();
  for (const o of orders) {
    const key = o.carrier?.toLowerCase() ?? "";
    const g = groups.get(key) ?? { carrier: o.carrier ?? null, pickupTime: carriers.find((c) => c.name.toLowerCase() === key)?.pickupTime ?? null, orders: [] };
    g.orders.push(o);
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => {
    const [ra, ka] = rank(a.carrier);
    const [rb, kb] = rank(b.carrier);
    return ra - rb || ka.localeCompare(kb);
  });
}
